/**
 * Image capture + preparation: camera or file picker, centre crop, downscale,
 * re-encode, and the average colour used as an instant placeholder.
 *
 * Everything happens on the device before a byte is uploaded. That is the cost
 * control for the whole image feature — storage is pennies, egress is the bill,
 * and egress is decided here by how big the file is when it leaves the phone.
 *
 * Capacitor packages are loaded with a dynamic import so the web bundle doesn't
 * try to resolve them statically when running in a non-native context.
 */

export interface CaptureOptions {
  /** Longest edge of the result, in px. */
  maxDimension: number
  /** Encoder quality, 0-1. */
  quality?: number
  /** width/height of the output; the source is centre-cropped to match. */
  aspect?: number
  /** Camera-first (photos of things) vs library-first (a picture of yourself). */
  preferCamera?: boolean
}

export interface PreparedImage {
  blob: Blob
  dataUrl: string
  width: number
  height: number
  /** '#rrggbb' mean pixel — the placeholder colour. */
  avgColor: string
}

/** A crop window in source-image pixels. */
export interface CropRect {
  sx: number
  sy: number
  sw: number
  sh: number
}

export interface PickedImage {
  dataUrl: string
  /**
   * True when the picker already cropped for us — the native Capacitor camera
   * opens the OS editor (`allowEditing`), and running our own cropper on top
   * of that would ask the user to frame the same photo twice.
   */
  alreadyCropped: boolean
}

type CameraModule = {
  Camera: { getPhoto: (opts: Record<string, unknown>) => Promise<{ dataUrl?: string }> }
}

// WebP is ~30% smaller than JPEG at the same perceived quality, which is a
// straight 30% off the egress line. Every browser we ship to supports it; the
// probe is for the jsdom/test case and ancient webviews.
let webpSupport: boolean | null = null
function supportsWebp(): boolean {
  if (webpSupport === null) {
    const canvas = document.createElement("canvas")
    canvas.width = 1
    canvas.height = 1
    webpSupport = canvas.toDataURL("image/webp").startsWith("data:image/webp")
  }
  return webpSupport
}

export function outputMimeType(): string {
  return supportsWebp() ? "image/webp" : "image/jpeg"
}

async function tryCapacitorCamera(preferCamera: boolean): Promise<string | null> {
  try {
    const { Capacitor } = await import("@capacitor/core")
    if (!Capacitor.isNativePlatform()) return null
    // Hidden behind a variable so Rollup doesn't statically resolve an
    // optional native dependency on web.
    const cameraPath = ["@capacitor", "camera"].join("/")
    const cameraModule = (await import(/* @vite-ignore */ cameraPath).catch(() => null)) as CameraModule | null
    if (!cameraModule) return null
    const result = await cameraModule.Camera.getPhoto({
      quality: 85,
      // The native crop UI, free: better than any cropper we'd build, and it
      // means the bytes are already the right shape before we re-encode.
      allowEditing: true,
      resultType: "dataUrl",
      source: preferCamera ? "PROMPT" : "PHOTOS",
    })
    return result.dataUrl ?? null
  } catch {
    return null
  }
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "")
    reader.readAsDataURL(file)
  })
}

function pickFromWeb(preferCamera: boolean): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    if (preferCamera) input.capture = "environment"
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) {
        resolve(null)
        return
      }
      resolve(await fileToDataUrl(file))
    }
    input.oncancel = () => resolve(null)
    input.click()
  })
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Could not read that image"))
    img.src = dataUrl
  })
}

/** Mean pixel of an image, as '#rrggbb'. */
function averageColor(img: CanvasImageSource): string {
  // Drawing the whole image into a 1x1 canvas makes the GPU do the averaging.
  const canvas = document.createElement("canvas")
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext("2d", { willReadFrequently: true })
  if (!ctx) return "#8a8a8a"
  ctx.drawImage(img, 0, 0, 1, 1)
  try {
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`
  } catch {
    // Tainted canvas (shouldn't happen for local data URLs) — fall back to a
    // neutral grey rather than failing the whole upload.
    return "#8a8a8a"
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not encode that image"))),
      mimeType,
      quality,
    )
  })
}

/**
 * Decide the source window to read out of an image: the cropper's explicit
 * rect when there is one, otherwise the centre cut for `aspect`.
 *
 * Pure and exported so the clamping is testable without a canvas. The clamping
 * is the whole point — `drawImage` given a rect that runs past the edge does
 * not error, it silently fills the overhang with transparent pixels, which
 * reaches the user as a black band down one side of their photo.
 */
export function resolveCrop(naturalWidth: number, naturalHeight: number, aspect?: number, crop?: CropRect): CropRect {
  let sx = 0
  let sy = 0
  let sw = naturalWidth
  let sh = naturalHeight
  if (sw <= 0 || sh <= 0) return { sx: 0, sy: 0, sw: Math.max(1, sw), sh: Math.max(1, sh) }

  if (crop && crop.sw > 0 && crop.sh > 0) {
    sx = Math.max(0, Math.min(Math.round(crop.sx), sw - 1))
    sy = Math.max(0, Math.min(Math.round(crop.sy), sh - 1))
    sw = Math.max(1, Math.min(Math.round(crop.sw), sw - sx))
    sh = Math.max(1, Math.min(Math.round(crop.sh), sh - sy))
  } else if (aspect) {
    // No explicit crop: the centre cut, which is what the native editor and
    // the pre-cropper behaviour both produce.
    //
    // Both cuts floor at 1px. An extreme source (a 1px-wide image against a
    // 3:1 banner) rounds the short side to 0, and drawImage with a zero-height
    // source draws nothing at all — a blank photo rather than a thin one.
    if (sw / sh > aspect) {
      const cropped = Math.max(1, Math.min(sw, Math.round(sh * aspect)))
      sx = Math.round((sw - cropped) / 2)
      sw = cropped
    } else {
      const cropped = Math.max(1, Math.min(sh, Math.round(sw / aspect)))
      sy = Math.round((sh - cropped) / 2)
      sh = cropped
    }
  }
  return { sx, sy, sw, sh }
}

/**
 * Centre-crop to `aspect`, downscale to `maxDimension`, re-encode, and measure.
 * Runs on whatever the picker returned, including images that are already small
 * — a 200 KB PNG screenshot has to become a 20 KB WebP too.
 */
export async function prepareImage(dataUrl: string, options: CaptureOptions, crop?: CropRect): Promise<PreparedImage> {
  const { maxDimension, quality = 0.75, aspect } = options
  const img = await loadImage(dataUrl)

  const { sx, sy, sw, sh } = resolveCrop(img.naturalWidth || img.width, img.naturalHeight || img.height, aspect, crop)

  const scale = Math.min(1, maxDimension / sw, maxDimension / sh)
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(sw * scale))
  canvas.height = Math.max(1, Math.round(sh * scale))
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Could not process that image")
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)

  const mimeType = outputMimeType()
  const blob = await canvasToBlob(canvas, mimeType, quality)
  return {
    blob,
    dataUrl: canvas.toDataURL(mimeType, quality),
    width: canvas.width,
    height: canvas.height,
    avgColor: averageColor(canvas),
  }
}

/**
 * Pick a photo without touching it. Resolves null when the user cancels.
 *
 * Split out from `captureImage` so the caller can put a cropper between the
 * picker and the encode — by the time `prepareImage` has run, the pixels the
 * user might have wanted are already gone.
 */
export async function pickImage(options: CaptureOptions): Promise<PickedImage | null> {
  const preferCamera = options.preferCamera ?? false
  const native = await tryCapacitorCamera(preferCamera)
  if (native) return { dataUrl: native, alreadyCropped: true }
  const web = await pickFromWeb(preferCamera)
  return web ? { dataUrl: web, alreadyCropped: false } : null
}

/**
 * Pick a photo and prepare it, centre-cropping. Used where there is no framing
 * decision to make — the caller wants bytes, not a dialog.
 */
export async function captureImage(options: CaptureOptions): Promise<PreparedImage | null> {
  const picked = await pickImage(options)
  if (!picked) return null
  return prepareImage(picked.dataUrl, options)
}

/** Presets — the sizes are the cost model (see docs/SHOPPING_LISTS_AND_IMAGES.md). */
export const AVATAR_OPTIONS: CaptureOptions = { maxDimension: 256, quality: 0.8, aspect: 1 }
export const COVER_OPTIONS: CaptureOptions = { maxDimension: 1024, quality: 0.75, aspect: 3, preferCamera: true }
// Receipts keep their shape (no aspect) — cropping one loses the total.
export const RECEIPT_OPTIONS: CaptureOptions = { maxDimension: 1280, quality: 0.7, preferCamera: true }
